<template>
  <div class="profile-account-contact-information text-center">
    <TopMenu
      :title="$t('PROFILE.CONTACT_INFORMATION.PAGE_LABEL')"
      back-to="/dashboard/profile"
      dark-background/>
    <div class="container-fluid content">
      <b-alert
        class="mt-3"
        show
        variant="info"
        v-html="$t('PROFILE.CONTACT_INFORMATION.INFORMATION_MESSAGE')" />
      <form
        class="form-contact-information mt-3"
        @submit.prevent="save">
        <FormInput
          v-validate="'required'"
          :name="$t('PROFILE.CONTACT_INFORMATION.FIRST_NAME')"
          v-model="memberContactInformation.firstName"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.FIRST_NAME')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'required'"
          :name="$t('PROFILE.CONTACT_INFORMATION.LAST_NAME')"
          v-model="memberContactInformation.lastName"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.LAST_NAME')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'required'"
          :name="$t('PROFILE.CONTACT_INFORMATION.ADDRESS')"
          v-model="memberContactInformation.address.street"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.ADDRESS')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-model="memberContactInformation.address.appartment"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.APPARTMENT')"
          class="mb-3"
          type="text"/>
        <FormSelect
          v-validate="'required'"
          v-model="memberContactInformation.address.countryCode"
          :name="$t('PROFILE.CONTACT_INFORMATION.COUNTRY')"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.COUNTRY')"
          :options="formatCountryToOptions(countryList)"
          required
          class="mb-3"/>
        <FormSelect
          v-validate="'required'"
          v-model="memberContactInformation.address.provinceCode"
          :name="$t('PROFILE.CONTACT_INFORMATION.PROVINCE')"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.PROVINCE')"
          :options="selectedProvinces"
          required
          class="mb-3" />
        <FormInput
          v-validate="'required'"
          :name="$t('PROFILE.CONTACT_INFORMATION.CITY')"
          v-model="memberContactInformation.address.city"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.CITY')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'required|postalCode'"
          v-mask="customMask"
          :name="$t('PROFILE.CONTACT_INFORMATION.POSTAL_CODE')"
          v-model="memberContactInformation.address.postalCode"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.POSTAL_CODE')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'required|phoneNumber'"
          v-mask="['(###) ###-####','#(###) ###-####']"
          :name="$t('PROFILE.CONTACT_INFORMATION.PHONE')"
          v-model="memberContactInformation.phoneNumber"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.PHONE')"
          required
          class="mb-3"
          type="tel"/>
        <FormInput
          v-validate="'phoneNumber'"
          v-mask="['(###) ###-####','#(###) ###-####']"
          :name="$t('PROFILE.CONTACT_INFORMATION.CELLPHONE')"
          v-model="memberContactInformation.mobileNumber"
          :placeholder="$t('PROFILE.CONTACT_INFORMATION.CELLPHONE')"
          class="mb-3"
          type="tel"/>
        <ActionButton
          type="submit"
          class="btn btn-lg btn-primary btn-block mt-3 mb-3">{{ $t("PROFILE.CONTACT_INFORMATION.SAVE_BUTTON") }}</ActionButton>
      </form>
    </div>
    <BottomMenu />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import TextLink from '@/components/Utils/TextLink';
import ActionButton from '@/components/Actions/ActionButton';
import FormInput from '@/components/Forms/FormInput';
import FormSelect from '@/components/Forms/FormSelect';

export default {
  name: 'ProfileContactInformation',
  components: {
    ActionButton,
    TextLink,
    TopMenu,
    BottomMenu,
    FormInput,
    FormSelect,
  },
  data() {
    return {
      memberContactInformation: {
        address: {},
      },
      customMask: {
        mask: 'UUUUUU',
        tokens: {
          U: {
            pattern: /[0-9a-zA-Z]/,
            transform: v => v.toUpperCase(),
          },
        },
      },
    };
  },
  computed: {
    ...mapState('localization', ['countriesWithProvinces']),
    ...mapState('member', ['selectedContactInformation']),
    selectedProvinces: {
      get() {
        if (!this.memberContactInformation.address.countryCode) {
          return [];
        }
        const country = this.countryList.filter(
          c => (c.code === this.memberContactInformation.address.countryCode),
        )[0];
        if (!country) {
          return [];
        }
        return country.provinces
          .map(province => ({
            value: province.code,
            text: this.$t(`PROVINCE.${province.code}`),
          })).sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));
      },
    },
  },
  watch: {
    countriesWithProvinces: {
      handler(newVal) {
        if (newVal) {
          this.countryList = newVal;
        } else {
          this.countryList = [];
        }
      },
      immediate: true,
    },
    selectedContactInformation: {
      handler(newVal) {
        if (newVal) {
          this.memberContactInformation = cloneDeep(newVal);
          if (!this.memberContactInformation.address) {
            this.memberContactInformation.address = {
              countryCode: 'CA',
              provinceCode: 'CA-QC',
            };
          }
        } else {
          this.memberContactInformation = {
            address: {},
          };
        }
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('localization/countriesWithProvinces').then(() => {
      this.$store.dispatch('member/currentMemberContactInformation');
    });
  },
  methods: {
    save() {
      this.$validator.validate().then(() => {
        if (this.$validator.errors.items.length > 0) {
          this.$store.commit('alerts/setAlert', {
            msg: this.$t(
              `VALIDATION.${
                this.$validator.errors.items[0].rule.toUpperCase()}`,
              [this.$validator.errors.items[0].field],
            ),
          });
        } else {
          this.$store
            .dispatch(
              'member/saveContactInformation',
              this.memberContactInformation,
            )
            .then(() => {
              this.$store.commit('alerts/setAlert', {
                msg: 'PROFILE.CONTACT_INFORMATION.CONFIRMATION_MESSAGE',
              });
            });
        }
      });
    },
    formatCountryToOptions() {
      return this.countryList.map(country => ({
        value: country.code,
        text: this.$t(`COUNTRY.${country.code}`),
      }));
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.profile-account-contact-information {
    height: 100%;
  }
  &.content {
    padding-top: $top-menu-height;
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
}
</style>
